import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, ShoppingCart, Users, TrendingUp } from 'lucide-react';

export function DashboardPage() {
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: apiService.getProducts
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['orders'],
    queryFn: apiService.getOrders
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: apiService.getUsers
  });

  // Calculate basic stats
  const stats = [
    {
      name: 'Total Products',
      value: products.length,
      icon: Package,
      color: 'bg-blue-500'
    },
    {
      name: 'Total Orders',
      value: orders.length,
      icon: ShoppingCart,
      color: 'bg-green-500'
    },
    {
      name: 'Total Customers',
      value: users.length,
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      name: 'Revenue',
      value: `$${orders.reduce((sum, order) => sum + (order.total_price || 0), 0).toFixed(2)}`,
      icon: TrendingUp,
      color: 'bg-yellow-500'
    }
  ];

  const recentOrders = orders.slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">Welcome to the GKR Tags admin portal</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Card key={stat.name}>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <CardDescription>Latest customer orders</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{order.order_number}</p>
                      <p className="text-sm text-gray-600">{order.contact_name}</p>
                      <p className="text-sm text-gray-500">{order.product_type} x {order.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${order.total_price}</p>
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        order.status === 'paid' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No orders yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <button className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors">
                <p className="font-medium">View All Orders</p>
                <p className="text-sm text-gray-600">Manage and process customer orders</p>
              </button>
              <button className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors">
                <p className="font-medium">Add New Product</p>
                <p className="text-sm text-gray-600">Create new product listings</p>
              </button>
              <button className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors">
                <p className="font-medium">Customer Management</p>
                <p className="text-sm text-gray-600">View and manage customer accounts</p>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}